'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import {
  EntityImage,
  focalPointToObjectPosition,
  getSortedImages,
} from '@/lib/entity-image';
import {
  playImageFocus,
  playImageReveal,
  selectedSlideImage,
} from '@/lib/image-reveal';
import { mergeClassNames } from '@/lib/utils';
import { useIsEmbed } from '@/contexts/embed';
import { ImageLightbox } from './image-lightbox';
import { RevealImage } from './reveal-image';

// The phone layout holds until md, with a taller strip between sm and md so
// the photo is not cut to a sliver, then a square beside the text.
const SINGLE_FRAME = 'sm:h-[360px] md:h-gallery md:w-gallery';
const ROW = 'sm:h-[360px] md:h-gallery';
const MAIN = 'md:order-2 md:w-gallery md:flex-none';

// Matches the photo's box, for a skeleton standing in for it.
export const GALLERY_SKELETON =
  '!h-[280px] w-full sm:!h-[360px] md:!h-gallery md:w-gallery';

// A touch screen keeps :hover after a tap, so the photo would stay zoomed once
// the viewer closes.
const ZOOM_ON_HOVER =
  'transition-transform duration-500 ease-out [@media(hover:hover)_and_(prefers-reduced-motion:no-preference)]:group-hover:scale-105';

const toLightboxImages = (
  images: EntityImage[],
  fallbackUrl?: string | null
) =>
  images.length > 0
    ? images.map((image) => ({ id: image.entity_image_id, url: image.url }))
    : fallbackUrl
      ? [{ id: fallbackUrl, url: fallbackUrl }]
      : [];

interface ImageGalleryProps {
  images: EntityImage[];
  alt: string;
  fallbackUrl?: string | null;
}

export const ImageGallery = ({
  images,
  alt,
  fallbackUrl,
}: ImageGalleryProps) => {
  const sortedImages = getSortedImages(images);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // An embed's frame would centre the full-screen viewer far below what the
  // visitor sees, so photos open only on the booking site itself.
  const zoomable = !useIsEmbed();
  const imageClass = mergeClassNames(
    'h-full w-full object-cover',
    zoomable && ZOOM_ON_HOVER
  );
  const thumbsRef = useRef<HTMLDivElement>(null);

  const hasMultipleImages = sortedImages.length > 1;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    containScroll: 'trimSnaps',
    duration: 0,
  });

  // Embla selects the new slide inside its pointer-up handler, before it emits
  // pointerUp, so a select while the pointer is down is a swipe. A thumbnail
  // click selects with no pointer on the slider and plays its own reveal.
  useEffect(() => {
    if (!emblaApi) return;
    let isPointerDown = false;
    const onPointerDown = () => {
      isPointerDown = true;
    };
    const onPointerUp = () => {
      isPointerDown = false;
    };
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      if (isPointerDown) playImageFocus(selectedSlideImage(emblaApi));
    };
    emblaApi.on('pointerDown', onPointerDown);
    emblaApi.on('pointerUp', onPointerUp);
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('pointerDown', onPointerDown);
      emblaApi.off('pointerUp', onPointerUp);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // scrollIntoView propagates across iframe boundaries and scrolls the host
  // page; replicate `block: 'nearest'` against the container's visual box so
  // the scroll stays contained.
  useEffect(() => {
    const container = thumbsRef.current;
    if (!container) return;
    const selectedThumb = container.children[selectedIndex] as
      | HTMLElement
      | undefined;
    if (!selectedThumb) return;

    const containerRect = container.getBoundingClientRect();
    const thumbRect = selectedThumb.getBoundingClientRect();
    const visualTop = thumbRect.top - containerRect.top;
    const visualBottom = thumbRect.bottom - containerRect.top;
    const viewHeight = container.clientHeight;

    if (visualTop < 0) {
      container.scrollTo({ top: container.scrollTop + visualTop });
    } else if (visualBottom > viewHeight) {
      container.scrollTo({
        top: container.scrollTop + (visualBottom - viewHeight),
      });
    }
  }, [selectedIndex]);

  // The slider jumps rather than slides (duration 0), so the new photo comes
  // into focus instead of cutting in.
  const handleThumbClick = useCallback(
    (index: number) => {
      if (!emblaApi || index === emblaApi.selectedScrollSnap()) return;
      emblaApi.scrollTo(index);
      playImageReveal(selectedSlideImage(emblaApi));
    },
    [emblaApi]
  );

  if (sortedImages.length === 0 && !fallbackUrl) return null;

  const lightbox = lightboxIndex !== null && (
    <ImageLightbox
      images={toLightboxImages(sortedImages, fallbackUrl)}
      alt={alt}
      startIndex={lightboxIndex}
      onClose={() => setLightboxIndex(null)}
    />
  );

  if (!hasMultipleImages) {
    const image = sortedImages[0];
    const imageUrl = image?.url ?? fallbackUrl;
    if (!imageUrl) return null;

    const objectPosition = image
      ? focalPointToObjectPosition(image.focal_point_x, image.focal_point_y)
      : '50% 50%';

    const singleImage = (
      <RevealImage
        src={imageUrl}
        alt={alt}
        className={imageClass}
        style={{ objectPosition }}
      />
    );
    const frameClass = mergeClassNames(
      'block h-[280px] w-full overflow-hidden rounded-xl bg-gray-100',
      SINGLE_FRAME
    );

    if (!zoomable) return <div className={frameClass}>{singleImage}</div>;

    return (
      <>
        <button
          type="button"
          aria-label={`Open photo of ${alt}`}
          onClick={() => setLightboxIndex(0)}
          className={mergeClassNames(
            frameClass,
            'mb-focus-visible group cursor-zoom-in'
          )}
        >
          {singleImage}
        </button>
        {lightbox}
      </>
    );
  }

  return (
    <div className={mergeClassNames('flex h-60 gap-2.5', ROW)}>
      <div
        ref={thumbsRef}
        className="order-2 flex shrink-0 flex-col gap-2.5 overflow-y-auto [scrollbar-width:none] md:order-1"
      >
        {sortedImages.map((image, index) => (
          <button
            key={image.entity_image_id}
            type="button"
            onClick={() => handleThumbClick(index)}
            className={mergeClassNames(
              'mb-focus-visible mb-focus-inset relative h-12 w-12 shrink-0 overflow-hidden rounded-[10px] transition-opacity sm:h-20 sm:w-20 md:h-12 md:w-12',
              selectedIndex === index
                ? 'opacity-100'
                : 'opacity-40 hover:opacity-60 focus-visible:opacity-100'
            )}
          >
            <img
              src={image.url}
              alt={`${alt} ${index + 1}`}
              className="h-full w-full object-cover"
              style={{
                objectPosition: focalPointToObjectPosition(
                  image.focal_point_x,
                  image.focal_point_y
                ),
              }}
            />
          </button>
        ))}
      </div>

      <div
        className={mergeClassNames(
          'order-1 min-w-0 flex-1 overflow-hidden rounded-xl bg-gray-100',
          MAIN
        )}
        ref={emblaRef}
      >
        <div className="flex h-full">
          {sortedImages.map((image, index) => {
            // Not lazy: the thumbnails already fetch every photo, and a lazy
            // slide waits until it is swiped to, then fades in from the cache
            // as if it were still loading.
            const slideImage = (
              <RevealImage
                src={image.url}
                alt={`${alt} ${index + 1}`}
                className={imageClass}
                style={{
                  objectPosition: focalPointToObjectPosition(
                    image.focal_point_x,
                    image.focal_point_y
                  ),
                }}
              />
            );

            // Embla swallows the click that ends a drag, so a swipe through the
            // photos never opens the viewer.
            return zoomable ? (
              <button
                key={image.entity_image_id}
                type="button"
                aria-label={`Open photo ${index + 1} of ${alt}`}
                onClick={() => setLightboxIndex(index)}
                className="mb-focus-visible mb-focus-inset group h-full w-full flex-[0_0_100%] cursor-zoom-in overflow-hidden"
              >
                {slideImage}
              </button>
            ) : (
              <div
                key={image.entity_image_id}
                className="h-full w-full flex-[0_0_100%]"
              >
                {slideImage}
              </div>
            );
          })}
        </div>
      </div>

      {lightbox}
    </div>
  );
};
